/**
 * Regional language editions (rule 27 hreflang, rule 66 earned indexing).
 *
 * `en` and `hi` are FULL locales: every slug has a message bundle, a URL prefix
 * policy and a slug-alias table (see `locales.ts`). A REGIONAL locale is a
 * different thing — a hand-authored edition of a SUBSET of slugs, published to
 * reach a language market without carrying a whole translated site. It has no
 * message bundle and never enters `Locale`.
 *
 * WHY THIS FILE EXISTS (2026-08-13). The "9-language regional Free Kundali
 * matrix" (a2b4c36bb) shipped five pages that each declared their metadata as a
 * raw object literal, so they bypassed BOTH governed metadata helpers and
 * therefore emitted no canonical, no hreflang and — critically — no `robots`.
 * That is the exact defect `seoPageMetadata`'s docstring records from 2026-08-09
 * (`/doshas/`, `/calculators/`, `/daily-tools/`, `/learn/` live and indexable
 * while the registry said unearned), reproduced four days later because nothing
 * FORCED a new literal page through a governed helper. The same commit also
 * linked to `/ta/` and `/te/`, which did not exist at the time — the declared
 * 9-language matrix was really 7 with two dead links (both editions were
 * created later). All seven were `noindex, follow` from 2026-09-23 because
 * none rendered the kundali in its own language; since 2026-10-21 the editions
 * in `IN_LANGUAGE_KUNDALI_LOCALES` below do, and are indexed (Odia was added
 * the same day).
 *
 * The fix is by-construction, not per-page care: `regionalPageMetadata()` in
 * `./index` is the only supported way to build a regional page's metadata, the
 * edition list here is the only place a language is declared, and which slugs
 * each edition actually serves is SCANNED from the filesystem
 * (`regional-twins.generated.ts`) so it can never drift from what is deployed.
 */

/**
 * Every published regional edition, in BCP-47 form. The directory under
 * `src/app/` is the tag itself (`src/app/mr/…` → `mr`).
 *
 * `scripts/sync-locale-twins.mjs` parses this array to decide which directories
 * to scan, so adding an edition here and creating its pages is the whole
 * onboarding: the twin map, hreflang cluster, language switcher and the
 * `<html lang>` pre-paint script all follow from it.
 */
export const REGIONAL_LOCALES = ["bn", "gu", "kn", "ml", "mr", "or", "ta", "te"] as const;

export type RegionalLocale = (typeof REGIONAL_LOCALES)[number];

/**
 * The `<html lang>` value for each edition. Identical to the tag today, but kept
 * explicit: a future edition may be published under a region-qualified tag
 * (`pa-IN`, `zh-Hant`) whose URL segment is not its language tag.
 */
export const regionalLocaleLang: Record<RegionalLocale, string> = {
  bn: "bn",
  gu: "gu",
  kn: "kn",
  ml: "ml",
  mr: "mr",
  or: "or",
  ta: "ta",
  te: "te",
};

/** Endonym shown in the language switcher — never a transliteration. */
export const regionalLocaleLabel: Record<RegionalLocale, string> = {
  bn: "বাংলা",
  gu: "ગુજરાતી",
  kn: "ಕನ್ನಡ",
  ml: "മലയാളം",
  mr: "मराठी",
  or: "ଓଡ଼ିଆ",
  ta: "தமிழ்",
  te: "తెలుగు",
};

const REGIONAL_LOCALE_SET: ReadonlySet<string> = new Set(REGIONAL_LOCALES);

export function isRegionalLocale(value: string): value is RegionalLocale {
  return REGIONAL_LOCALE_SET.has(value);
}

/**
 * The edition a pathname belongs to, or `null` for `en`/`hi`/anything else.
 * Matches only a full first segment, so `/mrigashira-nakshatra/` is not `mr`.
 */
export function detectRegionalLocale(pathname: string): RegionalLocale | null {
  const segment = pathname.replace(/^\/+/, "").split("/")[0] ?? "";
  return isRegionalLocale(segment) ? segment : null;
}

/** Canonical path for `slug` in a regional edition. Always trailing-slashed. */
export function regionalPath(locale: RegionalLocale, slug: string): string {
  return `/${locale}/${slug}/`;
}

/**
 * Editions whose kundali tool renders in their own language: the form (labels,
 * hints, validation, loading) and the computed chart (graha, rashi, nakshatra,
 * bhava, dasha and dosha names, and the chart, table and dasha headings).
 *
 * This is the exit rule `crawl-policy.ts` set on 2026-09-23 for the regional
 * free-kundali editions: an edition may be indexed once it renders the form
 * and the chart in its own language. `tests/regional-edition-indexability.test.ts`
 * holds `publicNoindexExactPaths` to exactly this list, so an edition cannot be
 * indexed without its tool being localized, nor localized and left out.
 * The vocabulary lives in `src/lib/jyotish-regional-terms.ts`, the form copy in
 * `KundaliFormCard.i18n.regional.ts` and the chart copy in
 * `src/components/kundali/regional-result-copy.ts`; each is keyed by this type.
 */
export const IN_LANGUAGE_KUNDALI_LOCALES = [
  "bn",
  "gu",
  "mr",
  "or",
  "ta",
] as const satisfies readonly RegionalLocale[];

export type KundaliRegionalLocale = (typeof IN_LANGUAGE_KUNDALI_LOCALES)[number];

const IN_LANGUAGE_KUNDALI_SET: ReadonlySet<string> = new Set(IN_LANGUAGE_KUNDALI_LOCALES);

/** The kundali display locale `value` names, when it is an in-language edition. */
export function kundaliRegionalLocale(
  value: string | null | undefined
): KundaliRegionalLocale | null {
  return value && IN_LANGUAGE_KUNDALI_SET.has(value) ? (value as KundaliRegionalLocale) : null;
}
